import { Model, Printer, PrinterImage, WebUSB } from "escpos-buffer-web";
import { Align, Cut } from "escpos-buffer-web";
import { toast } from "react-toastify";

interface Variation {
  typeName: string;
  optionName: string;
  price: number;
  uniqueNumber: string | null;
}

interface CartItem {
  id: number;
  serviceId: number;
  name: string;
  basePrice: number;
  variations: Variation[];
  finalPrice: number;
  quantity: number;
  discount: number;
  discountInPercentage: number;
  type: string;
}

interface Service {
  name: string;
  price: number;
  quantity: number;
  discount: number;
  total?: number;
  relatedService?: {
    services: {
      serviceName: string;
      price: number;
    };
  }[];
}

interface PrintData {
  customerName: string;
  cashierName: string;
  services: CartItem[] | Service[] | any[];
  GrandTotal: number;
  praTax: number;
  discountInPercentage: number;
  discountInAmount: number;
  amountPaid: number;
  pending: number;
  customReceiptId: string;
}

interface PrintStaffData {
  customerName: string;
  cashierName: string;
  services: CartItem[] | Service[] | any[];
  GrandTotal: number;
  praTax: number;
  discountInPercentage: number;
  discountInAmount: number;
  amountPaid: number;
  pending: number;
  customReceiptId: string;
  date?: string;
}

// Load and preprocess logo
const loadImageAsBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
};

/**
 * Resizes a base64 image to targetWidth and returns new base64
 */
export const resizeBase64Image = (
  base64: string,
  targetWidth: number = 450
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64;

    img.onload = () => {
      const aspectRatio = img.height / img.width;
      const targetHeight = Math.floor(targetWidth * aspectRatio);

      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext("2d")!;
      ctx.imageSmoothingEnabled = false;

      // Removed filter to prevent washout
      // ctx.filter = 'contrast(160%) brightness(120%)';

      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const brightness =
          data[i] * 0.5 + data[i + 1] * 0.35 + data[i + 2] * 0.15;

        const bw = brightness > 140 ? 255 : 0;

        data[i] = data[i + 1] = data[i + 2] = bw;
        data[i + 3] = 255;
      }

      ctx.putImageData(imageData, 0, 0);
      const resizedBase64 = canvas.toDataURL("image/png");
      resolve(resizedBase64);
    };

    img.onerror = (error) => reject(error);
  });
};

export const loadLogo = async (logoUrl: string): Promise<{ image: any }> => {
  // Step 1: Load image as base64
  const base64 = await loadImageAsBase64(logoUrl);

  // Step 2: Resize to 384px width
  const resizedBase64 = await resizeBase64Image(base64, 450); // or 200, 576 depending on printer

  // Step 3: Print the resized image
  const image = await new PrinterImage(resizedBase64);
  return { image };
};

async function connectToPosPrinter(): Promise<USBDevice> {
  try {
    const filters = [
      { vendorId: 0x0416 },
      { vendorId: 0x04b8 },
      { vendorId: 0x1a86 },
    ];

    let device = (await navigator.usb.getDevices()).find((d) =>
      filters.some((f) => f.vendorId === d.vendorId)
    );

    if (!device) {
      // requestDevice MUST be triggered by a direct user click (onPrint)
      device = await navigator.usb.requestDevice({ filters });
    }

    // Ensure the device is connected
    if (!device.opened) {
      await device.open();
    }

    // Select Configuration and Claim Interface (Crucial for "Access Denied")
    // Most printers use configuration 1 and interface 0
    await device.selectConfiguration(1);
    await device.claimInterface(
      device.configuration?.interfaces[0].interfaceNumber ?? 0
    );

    return device;
  } catch (error) {
    console.error("Connection failed:", error);
    throw error;
  }
}

function wrapText(text: string, width: number): string[] {
  const regex = new RegExp(`.{1,${width}}`, "g");
  return text.match(regex) || [];
}

// Replaces printReceiptsDirect
export const printReceiptsDirect = async (
  printData: PrintData
): Promise<{ success: boolean; message: string }> => {
  try {
    const { image } = await loadLogo("/logo.png");

    const device = await connectToPosPrinter();
    const model = new Model("POS-80");
    const connection = new WebUSB(device);
    await connection.open();
    const printer = new Printer(model, connection);

    // ============================================
    // CUSTOMER RECEIPT
    // ============================================
    printer.columns = 46;
    printer.alignment = Align.Center;

    await new Promise((resolve) => setTimeout(resolve, 500));
    printer.draw(image);
    await new Promise((resolve) => setTimeout(resolve, 1000));

    printer.withStyle({ bold: true, width: 2, height: 2 }, () => {
      printer.alignment = Align.Center;
      printer.feed(2);
    });

    printer.writeln("2nd Floor, Plaza 147, Central Commercial");
    printer.writeln("Bahria Orchard, Lahore, Pakistan");
    printer.writeln("Phone: +923345555882");
    printer.feed(1);

    printer.withStyle({ bold: true }, () => {
      printer.writeln("CUSTOMER RECEIPT");
    });
    printer.feed(1);

    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    printer.alignment = Align.Left;
    printer.writeln(`PNTN #: 5915388-3`);
    printer.writeln(`Receipt Id:  ${printData.customReceiptId || "--"}`);
    printer.writeln(`Customer: ${printData.customerName || "--"}`);
    printer.writeln(`Date:     ${formattedDate}`);
    printer.writeln(`Cashier:  ${printData.cashierName || "--"}`);
    printer.feed(1);
    printer.drawLine();

    const cw = { name: 20, qty: 3, base: 7, tax: 6, total: 10 };
    let totalBase = 0;
    let totalDiscount = 0;

    printer.withStyle({ width: 1, height: 1 }, () => {
      printer.writeln(
        "SERVICE".padEnd(cw.name) +
          "QTY".padStart(cw.qty) +
          "BASE".padStart(cw.base) +
          "TAX".padStart(cw.tax) +
          "TOTAL".padStart(cw.total)
      );
      printer.drawLine();

      printData.services.forEach((s: any) => {
        const isCartItem = Array.isArray(s.variations);

        // FIX: Check both 'qty' (from your state) and 'quantity'
        const currentQty = s.qty || s.quantity || 1;

        let itemTotal: number;
        let serviceName: string;

        if (isCartItem) {
          itemTotal = s.finalPrice * currentQty - (s.discount || 0);
          serviceName = s.name;

          if (s.variations && s.variations.length > 0) {
            const variationLabels = s.variations
              .map((v: any) => `${v.optionName} ${v.typeName}`)
              .join(", ");

            const hasUniqueNumber = s.variations.some(
              (v: any) => v.uniqueNumber !== null
            );

            if (hasUniqueNumber) {
              serviceName = `${s.name} (${variationLabels})`;
            } else {
              serviceName = `${s.name}`;
            }
          }
        } else {
          itemTotal = Number(
            s.total || s.price * currentQty - (s.discount || 0)
          );
          serviceName = s.name || "Service";
        }

        const itemBaseTotal = itemTotal / 1.08;
        const itemTaxTotal = itemTotal - itemBaseTotal;

        totalBase += itemBaseTotal;
        totalDiscount += s.discount || 0;

        const nameLines = wrapText(serviceName, cw.name);

        printer.writeln(
          nameLines[0].padEnd(cw.name) +
            currentQty.toString().padStart(cw.qty) +
            itemBaseTotal.toFixed(0).padStart(cw.base) +
            itemTaxTotal.toFixed(0).padStart(cw.tax) +
            itemTotal.toFixed(0).padStart(cw.total)
        );

        for (let i = 1; i < nameLines.length; i++) {
          printer.writeln(nameLines[i].padEnd(cw.name));
        }
      });

      printer.drawLine();
    });

    printer.alignment = Align.Right;
    printer.feed(1);

    printer.writeln(`Sub Total (excl. Tax): Rs. ${totalBase.toFixed(0)}`);

    // UPDATED: Show Discount Amount and Percentage if available
    if (totalDiscount > 0 || printData.discountInAmount > 0) {
      const discountAmount =
        totalDiscount > 0 ? totalDiscount : printData.discountInAmount;
      const discPerc = printData.discountInPercentage;
      const label =
        discPerc && discPerc > 0 ? `Discount (${discPerc}%):` : `Discount:`;

      printer.writeln(`${label} -Rs. ${discountAmount.toFixed(0)}`);
    }

    printer.writeln(`Total Tax (8%): Rs. ${printData.praTax.toFixed(0)}`);

    printer.feed(1);
    printer.withStyle({ bold: true, width: 2, height: 2 }, () => {
      printer.writeln(
        `GRAND TOTAL: Rs. ${Number(printData.GrandTotal).toFixed(0)}`
      );
    });

    // ============================================
    // NEW: PAYMENT DETAILS SECTION
    // ============================================
    printer.feed(1);
    printer.alignment = Align.Right;
    printer.withStyle({ bold: true }, () => {
      printer.writeln(
        `Amount Paid: Rs. ${Number(printData.amountPaid || 0).toFixed(0)}`
      );

      // Only show Pending if it's greater than 0
      if (Number(printData.pending) > 0) {
        printer.writeln(
          `Pending Amount: Rs. ${Number(printData.pending).toFixed(0)}`
        );
      } else {
        printer.writeln(`Balance: Rs. 0 (Fully Paid)`);
      }
    });

    printer.feed(2);
    printer.alignment = Align.Center;
    printer.writeln("Thank you for choosing Elegance!");
    printer.writeln("Visit us again soon!");
    printer.feed(4);

    await printer.cutter(Cut.Partial);
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // ============================================
    // STAFF RECEIPT
    // ============================================
    printer.columns = 46;
    printer.alignment = Align.Center;
    printer.draw(image);

    printer.withStyle({ bold: true, width: 2, height: 2 }, () => {
      printer.feed(2);
    });

    printer.writeln("STAFF RECEIPT");
    printer.feed(1);

    printer.alignment = Align.Left;
    printer.writeln(`Receipt Id:  ${printData.customReceiptId || "--"}`);
    printer.writeln(`Customer: ${printData.customerName || "--"}`);
    printer.writeln(`Date:     ${formattedDate}`);
    printer.writeln(`Cashier:  ${printData.cashierName || "--"}`);
    printer.feed(1);
    printer.drawLine();

    printer.withStyle({ width: 1, height: 1 }, () => {
      const staffCw = { name: 30, qty: 16 };
      printer.writeln(
        "SERVICE".padEnd(staffCw.name) + "QTY".padStart(staffCw.qty)
      );
      printer.drawLine();

      printData.services.forEach((s: any) => {
        const isCartItem = Array.isArray(s.variations);
        const currentQty = s.qty || s.quantity || 1; // FIX for Staff Receipt as well

        let serviceName = s.name || "Service";

        if (isCartItem && s.variations && s.variations.length > 0) {
          const variationLabels = s.variations
            .map((v: any) => `${v.optionName} ${v.typeName}`)
            .join(", ");
          serviceName = `${s.name} (${variationLabels})`;
        }

        const nameLines = wrapText(serviceName, staffCw.name);

        printer.writeln(
          nameLines[0].padEnd(staffCw.name) +
            currentQty.toString().padStart(staffCw.qty)
        );

        for (let i = 1; i < nameLines.length; i++) {
          printer.writeln(nameLines[i].padEnd(staffCw.name));
        }
      });
      printer.drawLine();
    });

    printer.feed(1);
    printer.withStyle({ bold: true, width: 2, height: 2 }, () => {
      printer.writeln(
        `GRAND TOTAL: Rs. ${Number(printData.GrandTotal).toFixed(0)}`
      );
    });

    printer.feed(4);
    await printer.cutter(Cut.Partial);
    await new Promise((resolve) => setTimeout(resolve, 3000));

    try {
      // First, close the high-level printer instance
      await printer.close();

      // Small buffer between printer and USB pipe closure
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Close the WebUSB connection
      await connection.close();

      // Only close the device if it's still marked as opened
      if (device && device.opened) {
        await device.close();
      }
    } catch (e) {
      // This catches the "Operation in progress" error silently
      // if the device already closed itself or is finishing a cut.
      console.log("Handled USB cleanup:", e);
    }

    return { success: true, message: "Printed successfully!" };
  } catch (error) {
    console.error("Print Error:", error);
    throw error;
  }
};

// Replaces printStaffReceiptsDirect
export const printStaffReceiptsDirect = async (
  printStaffData: PrintStaffData
): Promise<{ success: boolean; message: string }> => {
  try {
    const { image } = await loadLogo("/logo.png");

    const device = await connectToPosPrinter();
    const model = new Model("POS-80");
    const connection = new WebUSB(device);
    await connection.open();
    const printer = new Printer(model, connection);

    // ============================================
    // STAFF RECEIPT
    // ============================================

    const today = new Date(printStaffData.date);

    const formattedDate = today.toLocaleDateString("en-GB", {
      day: "numeric",

      month: "long",

      year: "numeric",
    });

    printer.columns = 46;
    printer.alignment = Align.Center;
    printer.draw(image);

    printer.withStyle({ bold: true, width: 2, height: 2 }, () => {
      printer.feed(2);
    });

    printer.writeln("STAFF RECEIPT");
    printer.feed(1);

    printer.alignment = Align.Left;
    printer.writeln(`Receipt Id:  ${printStaffData.customReceiptId || "--"}`);
    printer.writeln(`Customer: ${printStaffData.customerName || "--"}`);
    printer.writeln(`Date:     ${printStaffData.date}`);
    printer.writeln(`Cashier:  ${printStaffData.cashierName || "--"}`);
    printer.feed(1);
    printer.drawLine();

    printer.withStyle({ width: 1, height: 1 }, () => {
      const staffCw = { name: 30, qty: 16 };
      printer.writeln(
        "SERVICE".padEnd(staffCw.name) + "QTY".padStart(staffCw.qty)
      );
      printer.drawLine();

      printStaffData.services.forEach((s: any) => {
        const isCartItem = Array.isArray(s.variations);
        const currentQty = s.qty || s.quantity || 1; // FIX for Staff Receipt as well

        let serviceName = s.name || "Service";

        if (isCartItem && s.variations && s.variations.length > 0) {
          const variationLabels = s.variations
            .map((v: any) => `${v.optionName} ${v.typeName}`)
            .join(", ");
          serviceName = `${s.name} (${variationLabels})`;
        }

        const nameLines = wrapText(serviceName, staffCw.name);

        printer.writeln(
          nameLines[0].padEnd(staffCw.name) +
            currentQty.toString().padStart(staffCw.qty)
        );

        for (let i = 1; i < nameLines.length; i++) {
          printer.writeln(nameLines[i].padEnd(staffCw.name));
        }
      });
      printer.drawLine();
    });

    printer.feed(1);
    printer.withStyle({ bold: true, width: 2, height: 2 }, () => {
      printer.writeln(
        `GRAND TOTAL: Rs. ${Number(printStaffData.GrandTotal).toFixed(0)}`
      );
    });

    printer.feed(4);
    await printer.cutter(Cut.Partial);
    await new Promise((resolve) => setTimeout(resolve, 3000));

    try {
      // First, close the high-level printer instance
      await printer.close();

      // Small buffer between printer and USB pipe closure
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Close the WebUSB connection
      await connection.close();

      // Only close the device if it's still marked as opened
      if (device && device.opened) {
        await device.close();
      }
    } catch (e) {
      // This catches the "Operation in progress" error silently
      // if the device already closed itself or is finishing a cut.
      console.log("Handled USB cleanup:", e);
    }

    return { success: true, message: "Printed successfully!" };
  } catch (error) {
    console.error("Print Error:", error);
    throw error;
  }
};
// Main wrapper to match original export expectation
export const printReceipts = async (printData: PrintData) => {
  // console.log("printDataprintData", printData);
  return await printReceiptsDirect(printData);
};

export const printStaffReceipts = async (printStaffData: PrintStaffData) => {
  // console.log("printDataprintData", printData);
  return await printStaffReceiptsDirect(printStaffData);
};

/* 
  Old fallback alternative print function removed as user requested 
  "use this approach" implicitly replacing the complex fallback logic 
  with the robust direct print. 
  However, if needed for window.print fallback, we'd need to re-add it.
  For now, I'm sticking to the user's provided structure which only had the direct print.
*/
